import {useDelete, useNotify, useRefresh, useResourceContext, useUpdate} from "react-admin";
import {FILE_TYPE, FileFormValue, FolderFormValue, PreparedFormFileValue} from "components/material/view/info/FileAttachmentForm/types";

const getFolderPath = (folderId: string | undefined, folders: FolderFormValue[] = []): string | undefined => {
  if (!folderId) return undefined;

  const folder = folders.find(({id}) => id === folderId);
  if (!folder?.name?.trim()) return undefined;

  if (!folder.parentId) return `/${folder.name.trim()}`;

  const parentPath = getFolderPath(folder.parentId, folders);
  return parentPath ? `${parentPath}/${folder.name.trim()}` : `/${folder.name.trim()}`;
};

const flattenFiles = (files: FileFormValue[], folders: FolderFormValue[] = []): FileFormValue[] => {
  const rootFiles = files.filter(({folderId}) => !folderId);
  const rootFolders = folders.filter(({parentId}) => !parentId);

  const getFolderFiles = (folder: FolderFormValue): FileFormValue[] => {
    const folderFiles = files.filter(({folderId}) => folderId === folder.id);
    const childFolders = folders.filter(({parentId}) => parentId === folder.id);

    return [
      ...folderFiles,
      ...childFolders.flatMap(getFolderFiles),
    ];
  };

  return [
    ...rootFiles,
    ...rootFolders.flatMap(getFolderFiles),
  ];
};

const prepareRecommendations = (recommendations: FileFormValue[] = []): FileFormValue[] => (
  recommendations.map((file) => ({
    ...file,
    fileType: FILE_TYPE.RECOMMENDATION,
    direction: undefined,
    folderId: undefined,
    path: undefined,
  }))
);

const hasAttachedFile = (file: FileFormValue): boolean => Boolean(file?.file?.rawFile || file?.file?.src);

const useFileHandlers = (recordId: string) => {
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [deleteFile, {isPending: isDeleting}] = useDelete();
  const [update, {isPending: isUpdating}] = useUpdate();

  const loading = isDeleting || isUpdating;

  const handleUpdateFiles = async (files: FileFormValue[]): Promise<void> => {
    await Promise.all(
      files.map((file, index) => (
        new Promise<void>((resolve, reject) => {
        const formData = new FormData();
        formData.append("name", file.name);
        formData.append("order", `${index}`);
        formData.append("fileType", file.fileType || FILE_TYPE.REGULAR);
        formData.append("direction", file.direction || "");
        if (file.path) {
          formData.append("path", file.path);
        }
        if (file.file?.rawFile) {
          formData.append("file", file.file.rawFile);
        }

        update(
          file.id ? `${resource}/${recordId}/file/${file.id}` : `${resource}/${recordId}/file`,
          {id: recordId, data: formData},
          {
            onSuccess: () => {
              console.log("All files updated successfully");
              resolve();
            },
            onError: (error) => {
              console.error(`File update error for file #${index + 1}:`, error);
              notify("Ошибка обновления файла", {type: "error"});
              reject(error);
            },
          }
        );
        })
      ))
    );
  };

  const handleDeleteFiles = async (files: PreparedFormFileValue[]): Promise<void> => {
    await Promise.all(
      files.map((file, index) => (
        new Promise<void>((resolve, reject) => {
        deleteFile(
          `${resource}/${recordId}/file`,
          {id: file.id},
          {
            onSuccess: () => {
              console.log("All files deleted successfully");
              resolve();
            },
            onError: (error) => {
              console.error(`File delete error for file #${index + 1}:`, error);
              notify("Ошибка удаления файлов", {type: "error"});
              reject(error);
            },
          }
        );
        })
      ))
    );
  };

  const handleSubmit = async (
    data: { files?: FileFormValue[], recommendations?: FileFormValue[], folders?: FolderFormValue[] },
    files: PreparedFormFileValue[]
  ): Promise<void> => {
    const recommendationFiles = prepareRecommendations(data.recommendations).filter(hasAttachedFile);
    const regularFiles = flattenFiles((data.files || []).map((file) => ({
      ...file,
      fileType: FILE_TYPE.REGULAR,
      path: getFolderPath(file.folderId, data.folders),
    })), data.folders).filter(hasAttachedFile);
    const updatedFiles = [...recommendationFiles, ...regularFiles];

    const filesToSave: FileFormValue[] = [];
    const filesToDelete: PreparedFormFileValue[] = [];

    const updatedFileIds = new Set(updatedFiles.map((file) => file.id).filter(Boolean));

    files.forEach((file) => {
      if (!updatedFileIds.has(file.id)) {
        filesToDelete.push(file);
      }
    });

    updatedFiles.forEach((file) => {
      filesToSave.push(file);
    });

    try {
      if (filesToDelete.length > 0) await handleDeleteFiles(filesToDelete);
      if (filesToSave.length > 0) await handleUpdateFiles(filesToSave);
      notify("Файлы успешно сохранены", {type: "success"});
      refresh();
    } catch (error) {
      console.error("File submit error:", error);
      notify("Ошибка при обработке файлов", {type: "error"});
    }
  };


  return {handleSubmit, loading};
};

export default useFileHandlers;
